import { handleHttpResponse } from "../../utils/http_response_utils";
import { pollCeleryTaskUntilTerminal } from "../celery";

// Checks usually finish in seconds, so poll faster than the Celery default
const POLL_INTERVAL_MS = 3000;

/**
 * Run the classification pipeline's pre-run data checks without starting a pipeline.
 *
 * Starts a Celery task and polls it until it finishes.
 *
 * @param {string[]} companies - Company names to check
 * @param {string[]} platforms - Platform categories; these decide which checks run
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @returns {Promise<Array>} Per-company results
 * @throws {Error} With a message to show when the checks couldn't be run
 */
export const runPreflightChecks = async (companies, platforms, startDate, endDate) => {
  const accessToken = localStorage.getItem("access-token");
  const response = await handleHttpResponse(
    await fetch("/api/run-preflight-checks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        companies,
        platforms,
        start_date: startDate,
        end_date: endDate,
      }),
    }),
  );
  if (!(response instanceof Response)) {
    throw new Error("Could not run checks.");
  }
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.error || "Could not run checks.");
  }

  const { outcome, result } = await pollCeleryTaskUntilTerminal(body.task_id, { intervalMs: POLL_INTERVAL_MS });
  if (outcome === "completed") {
    return result?.results ?? [];
  }
  if (outcome === "failed") {
    throw new Error(result?.error ? `Checks failed: ${result.error}` : "Checks failed.");
  }
  if (outcome === "timeout") {
    throw new Error("Checks are taking too long. Is the Celery worker running?");
  }
  throw new Error("Could not run checks.");
};

/**
 * Run the checks for a pipeline batch.
 *
 * @returns {Promise<Array|null>} Per-company results, or null if the checks couldn't be run
 *   — the caller decides what to do then.
 */
export const runPreflightChecksForBatch = async (companies, platforms, startDate, endDate) => {
  try {
    return await runPreflightChecks(companies, platforms, startDate, endDate);
  } catch {
    return null;
  }
};
